import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, dateFnsLocalizer, Views, type View } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Plus } from 'lucide-react';
import { useCreateVisit, useVisitList } from './hooks';
import { usePageTitle } from '../../hooks/usePageTitle';
import { VisitForm } from './VisitForm';

const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

import type { VisitListItem } from './types';

export interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay?: boolean;
  resource: { status: string; customerId: string };
}

/// <summary>
/// Maps a VisitListItem onto a react-big-calendar event. Multi-day visits
/// must set allDay=true — without it, month-view treats them as time-positioned
/// single-day events and silently drops them from the grid. Exported as a pure
/// function so the mapping can be unit-tested without rendering the calendar.
/// </summary>
export function visitToEvent(v: VisitListItem): CalendarEvent {
  const start = new Date(v.startUtc);
  const end = new Date(v.endUtc);
  const crossesDay =
    start.getFullYear() !== end.getFullYear() ||
    start.getMonth() !== end.getMonth() ||
    start.getDate() !== end.getDate();
  return {
    id: v.id,
    title: v.customerName + (v.locationAddress ? ' — ' + v.locationAddress : ''),
    start,
    end,
    allDay: crossesDay,
    resource: { status: v.status, customerId: v.customerId }
  };
}

/// <summary>
/// Maps react-big-calendar's onRangeChange argument to the API query window.
/// RBC reports either an array of the visible day cells (week/day views) or a
/// { start, end } pair (month/agenda). We take the first/last instant and pad
/// ±1 day so visits on the boundary cells load. Exported as a pure function so
/// the window math can be unit-tested without rendering the calendar. This
/// replaced the old date ± fixed-offset window that shifted to month-end and
/// dropped earlier-month visits.
/// </summary>
export function visibleRange(
  rng: Date[] | { start: Date; end: Date },
): { from: string; to: string } {
  const start = Array.isArray(rng) ? rng[0] : rng.start;
  const end = Array.isArray(rng) ? rng[rng.length - 1] : rng.end;
  const from = new Date(start); from.setDate(from.getDate() - 1);
  const to = new Date(end); to.setDate(to.getDate() + 1);
  return { from: from.toISOString(), to: to.toISOString() };
}

export function CalendarPage() {
  usePageTitle('Calendar');
  const nav = useNavigate();
  const [view, setView] = useState<View>(Views.MONTH);
  const [date, setDate] = useState<Date>(new Date());
  const [creating, setCreating] = useState<{ startLocal: string; endLocal: string } | null>(null);

  // Track react-big-calendar's actual visible window via onRangeChange rather
  // than anchoring on `date` ± fixed offsets. `date` shifts to the end of the
  // visible month after mount, which fired two competing fetches whose second
  // window excluded earlier-month visits. RBC doesn't fire onRangeChange on
  // mount, so seed with a generous window around now.
  const [range, setRange] = useState<{ from: string; to: string }>(() => {
    const now = new Date();
    const from = new Date(now); from.setDate(from.getDate() - 14);
    const to = new Date(now); to.setDate(to.getDate() + 45);
    return { from: from.toISOString(), to: to.toISOString() };
  });

  const handleRangeChange = (rng: Date[] | { start: Date; end: Date }) => {
    setRange(visibleRange(rng));
  };

  const { data: visits } = useVisitList(range);

  const events: CalendarEvent[] = (visits ?? []).map(visitToEvent);

  const create = useCreateVisit();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Calendar</h1>
          <p className="text-slate-500 text-sm mt-1">{visits?.length ?? 0} visit{visits?.length === 1 ? '' : 's'} in view</p>
        </div>
        <button
          type="button"
          onClick={() => {
            const start = new Date();
            start.setMinutes(0, 0, 0);
            start.setHours(start.getHours() + 1);
            const end = new Date(start);
            end.setHours(end.getHours() + 1);
            setCreating({
              startLocal: toLocalInput(start),
              endLocal: toLocalInput(end)
            });
          }}
          className="hidden sm:inline-flex items-center gap-2 bg-sky-600 text-white px-3 py-2 rounded hover:bg-sky-700 text-sm"
        >
          <Plus size={16} />
          New visit
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-2 sm:p-4">
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          view={view}
          onView={setView}
          date={date}
          onNavigate={setDate}
          onRangeChange={handleRangeChange}
          views={[Views.MONTH, Views.WEEK, Views.DAY]}
          style={{ height: 600 }}
          selectable
          onSelectSlot={(slot) => {
            setCreating({
              startLocal: toLocalInput(slot.start as Date),
              endLocal: toLocalInput(slot.end as Date)
            });
          }}
          onSelectEvent={(ev) => nav(`/visits/${ev.id}`)}
          eventPropGetter={(ev) => ({ style: colorFor(ev.resource.status) })}
        />
      </div>

      {creating && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-end sm:items-center justify-center z-40" onClick={() => setCreating(null)}>
          <div
            className="bg-white rounded-t-xl sm:rounded-xl w-full sm:max-w-xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4">New visit</h2>
              <VisitForm
                defaultValues={creating}
                submitLabel="Schedule"
                submitting={create.isPending}
                errorMessage={errorMsg}
                onSubmit={async (payload) => {
                  setErrorMsg(null);
                  try {
                    const created = await create.mutateAsync(payload as Parameters<typeof create.mutateAsync>[0]);
                    setCreating(null);
                    nav(`/visits/${created.id}`);
                  } catch (err) {
                    setErrorMsg(readConflictMessage(err) ?? 'Save failed — try again.');
                  }
                }}
                onCancel={() => setCreating(null)}
              />
            </div>
          </div>
        </div>
      )}

      <Link
        to="#"
        onClick={(e) => {
          e.preventDefault();
          const start = new Date();
          start.setMinutes(0, 0, 0);
          start.setHours(start.getHours() + 1);
          const end = new Date(start);
          end.setHours(end.getHours() + 1);
          setCreating({
            startLocal: toLocalInput(start),
            endLocal: toLocalInput(end)
          });
        }}
        className="sm:hidden fixed bottom-6 right-6 bg-sky-600 text-white p-4 rounded-full shadow-lg hover:bg-sky-700"
        aria-label="New visit"
      >
        <Plus size={24} />
      </Link>
    </div>
  );
}

function toLocalInput(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return (
    date.getFullYear() +
    '-' +
    pad(date.getMonth() + 1) +
    '-' +
    pad(date.getDate()) +
    'T' +
    pad(date.getHours()) +
    ':' +
    pad(date.getMinutes())
  );
}

function colorFor(status: string) {
  switch (status) {
    case 'Scheduled':
      return { backgroundColor: '#0ea5e9', borderColor: '#0ea5e9', color: '#fff' };
    case 'InProgress':
      return { backgroundColor: '#f59e0b', borderColor: '#f59e0b', color: '#fff' };
    case 'Completed':
      return { backgroundColor: '#10b981', borderColor: '#10b981', color: '#fff' };
    case 'Cancelled':
      return { backgroundColor: '#94a3b8', borderColor: '#94a3b8', color: '#fff' };
    default:
      return {};
  }
}

function readConflictMessage(err: unknown): string | null {
  if (!axios.isAxiosError(err)) return null;
  const data = err.response?.data as { message?: string } | undefined;
  return data?.message ?? null;
}
